import { router } from '@/app';
import { authenticationService } from '@/services/authentication';
import { SignInResponse } from '@/services/authentication/types';
import { AuthUser, FetchUserAttributesOutput } from 'aws-amplify/auth';
import { AuthContextType } from './types';

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [attributes, setAttributes] =
    useState<FetchUserAttributesOutput | null>(null);
  const [groups, setGroups] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(true);

  const signInFirstStep = async (
    email: string,
    password: string
  ): Promise<SignInResponse> => {
    setLoading(true);

    const result = await authenticationService.signInFirstStep(email, password);

    setLoading(false);
    return result;
  };

  const signInWithMfaCode = async (code: string): Promise<SignInResponse> => {
    setLoading(true);
    const result = await authenticationService.confirmSignInWithEmailCode(code);
    if (result.isSignedIn) {
      await checkUserSession();
      authenticationService.confirmFirstAccess().catch(() => {});
    }
    setLoading(false);
    return result;
  };

  const signInWithNewPasswordRequired = async (
    password: string
  ): Promise<SignInResponse> => {
    setLoading(true);
    const result =
      await authenticationService.confirmSignInWithNewPasswordRequired(
        password
      );
    await checkUserSession();
    if (result.isSignedIn) {
      authenticationService.confirmFirstAccess().catch(() => {});
    }
    setLoading(false);
    return result;
  };

  const resendMfaCode = async (): Promise<void> => {
    router.navigate({ to: '/login' });
  };

  const requestNewLink = async (_email: string): Promise<SignInResponse> => {
    setLoading(true);

    try {
      setLoading(false);
      return {
        isSignedIn: false,
        nextStep: 'NONE',
        status: 200,
        message: 'Novo link enviado com sucesso.',
        isLinkRequested: true,
      };
    } catch (error) {
      console.error(error);
      setLoading(false);
      return {
        isSignedIn: false,
        nextStep: 'NONE',
        status: 500,
        message: 'Erro ao solicitar novo link.',
        isLinkRequested: false,
      };
    }
  };

  const getCurrentUserValues = async (): Promise<{
    user: AuthUser | null;
    attributes: FetchUserAttributesOutput | null;
  } | null> => {
    const result = await authenticationService.getCurrentUser();
    return result;
  };

  const checkUserSession = useCallback(async () => {
    setLoading(true);
    try {
      const result = await authenticationService.getCurrentUser();

      if (result && result.user) {
        setUser(result.user);
      } else {
        setUser(null);
      }

      if (result?.attributes) {
        setAttributes(result?.attributes);
      } else {
        setAttributes(null);
      }

      const session = await authenticationService.getAuthSession();
      if (session?.payload?.['cognito:groups']) {
        const cognitoGroups = session.payload['cognito:groups'];
        const userGroups: string[] = Array.isArray(cognitoGroups)
          ? (cognitoGroups as string[])
          : typeof cognitoGroups === 'string'
            ? [cognitoGroups]
            : [];
        setGroups(userGroups.length > 0 ? userGroups : null);
      } else {
        setGroups(null);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Erro ao verificar sessão:', error);
      setUser(null);
      setAttributes(null);
      setGroups(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkUserSession();
  }, [checkUserSession]);

  const logout = async () => {
    await authenticationService.signOutCurrentUser();
    setUser(null);
    setAttributes(null);
    setGroups(null);
  };

  const checkUserPermissions = async (value: string[]): Promise<boolean> => {
    try {
      const session = await authenticationService.getAuthSession();
      if (!session) return false;

      const permission = session.payload.permissions;
      if (!permission) return false;

      let permissions: string[];
      if (Array.isArray(permission)) {
        permissions = permission as string[];
      } else if (typeof permission === 'string') {
        try {
          permissions = JSON.parse(permission) as string[];
        } catch {
          permissions = permission.split(',').map(p => p.trim());
        }
      } else {
        return false;
      }

      return value.some(v => permissions.includes(v));
    } catch (error) {
      console.error('Erro ao verificar permissões:', error);
      return false;
    }
  };

  const checkUserGroups = async (groups: string[]): Promise<boolean> => {
    try {
      const session = await authenticationService.getAuthSession();
      if (!session) return false;

      const cognitoGroups = session.payload['cognito:groups'];
      if (!cognitoGroups) return false;

      let userGroups: string[];
      if (Array.isArray(cognitoGroups)) {
        userGroups = cognitoGroups as string[];
      } else if (typeof cognitoGroups === 'string') {
        userGroups = [cognitoGroups];
      } else {
        return false;
      }

      return groups.some(group => userGroups.includes(group));
    } catch (error) {
      console.error('Erro ao verificar grupos:', error);
      return false;
    }
  };

  const value: AuthContextType = {
    user,
    attributes,
    groups,
    isAuthenticated: !!user,
    signInFirstStep,
    signInWithMfaCode,
    signInWithNewPasswordRequired,
    resendMfaCode,
    requestNewLink,
    checkUserSession,
    getCurrentUserValues,
    logout,
    loading,
    checkUserPermissions,
    checkUserGroups,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
